import { canonicalSemanticJson } from './semanticGit'
import { fnv1a64 } from './similarity'
import { reasoningOf, type ModelStamp, type Session } from './types'
import type { AgendaAction } from './agenda'

export const ASSESSMENT_LIMITS = {
  workspaceBytes: 6_000_000,
  packetBytes: 400_000,
  attachmentBytes: 256_000,
  responseBytes: 100_000,
  attempts: 64,
  attachments: 12,
  findings: 5,
  actions: 3,
  maxTokens: 12_000,
} as const
export const COMPARISON_DIMENSIONS = [
  'objects',
  'encoding',
  'operations',
  'hypotheses',
  'scope',
] as const
export type AssessmentMode = 'research' | 'originality'
export interface AssessmentAttachment {
  id: string
  title: string
  kind: 'argument' | 'checker' | 'certificate' | 'source-text' | 'observation'
  provenance: 'user-supplied' | 'ai-authored' | 'local-execution'
  content: string
}
export interface AssessmentReference {
  id: string
  label: string
  kind: 'source' | 'theorem' | 'experiment' | 'attachment' | 'evidence' | 'obligation'
  revision: string
  provenance: string
  content: string
  attachmentKind?: AssessmentAttachment['kind']
}
export interface AssessmentPacket {
  version: 1
  sessionId: string
  runId: string
  mode: AssessmentMode
  inputRevision: string
  revision: string
  context: string
  nodeIds: string[]
  references: AssessmentReference[]
  focusNodeId?: string
  missingContext: string[]
}
export interface AssessmentFinding {
  id: string
  nodeIds: string[]
  statement: string
  correctness: { status: 'supported' | 'unsupported' | 'unresolved'; explanation: string }
  originality: { status: 'known' | 'candidate-distinction' | 'unresolved'; explanation: string }
  significance: string
  usefulness: string
  scope: string
  limitations: string[]
  evidenceIds: string[]
}
export interface OriginalityComparison {
  findingId: string
  referenceId: string
  theoremLocator: string
  relationship: 'equivalent' | 'subsumed-by' | 'candidate-distinction' | 'unresolved'
  mappings: {
    dimension: (typeof COMPARISON_DIMENSIONS)[number]
    ours: string
    prior: string
    correspondence: string
    gap: string
  }[]
  translationObligation: string
  unresolved: string[]
}
export interface RetainedResearchLesson {
  id: string
  failedNodeId: string
  findingId: string
  failedAssumption: string
  scope: string
  evidenceIds: string[]
  repairRequirements: string[]
}
export interface AssessmentAction {
  id: string
  findingId: string
  nodeId: string
  action: AgendaAction
  question: string
  purpose: string
  evidenceIds: string[]
  lessonIds: string[]
  targetObligationId: string | null
  outcomes: { supporting: string; challenging: string; inconclusive: string }
}
export interface RunAssessment {
  findings: AssessmentFinding[]
  comparisons: OriginalityComparison[]
  lessons: RetainedResearchLesson[]
  next: AssessmentAction[]
  missingContext: string[]
}
export interface AssessmentAttempt {
  id: string
  opId: string
  createdAt: number
  completedAt: number | null
  model: ModelStamp
  packet: AssessmentPacket
  state: 'pending' | 'completed' | 'incomplete' | 'unknown' | 'refused'
  result: RunAssessment | null
  issue: string | null
  response?: { content: string | null; bytes: number; fingerprint: string }
}
export interface RunAssessmentWorkspace {
  version: 1
  attempts: AssessmentAttempt[]
}

export const assessmentFingerprint = (value: unknown) => fnv1a64(canonicalSemanticJson(value))
export const assessmentBytes = (value: unknown) =>
  new TextEncoder().encode(JSON.stringify(value)).length

/** A review includes every surviving result and premise of its selected run.
 * Labels, coordinates and self-ratings cannot change its scientific inputs. */
export function assessmentNodeIds(session: Session, runId: string, focusNodeId?: string): string[] {
  const run = session.runs.find((r) => r.id === runId)
  if (!run) return []
  const opIds = new Set(run.moves.map((m) => m.opId))
  const pending = focusNodeId
    ? [focusNodeId]
    : session.ops
        .filter((op) => opIds.has(op.id))
        .flatMap((op) => [...op.targetIds, ...op.produced])
  const seen = new Set<string>()
  while (pending.length) {
    const id = pending.pop()!
    if (seen.has(id)) continue
    const node = session.nodes[id]
    if (!node) continue
    seen.add(id)
    if (node.parentId) pending.push(node.parentId)
    pending.push(...(node.bridgeFrom ?? []))
  }
  return session.order.filter((id) => seen.has(id))
}

export function assessmentInputRevision(session: Session, runId: string) {
  return assessmentFingerprint({
    sessionId: session.id,
    seed: session.seed,
    frame: session.frame,
    brief: session.brief,
    run: session.runs.find((r) => r.id === runId) ?? null,
    nodes: session.order
      .filter((id) => !!session.nodes[id])
      .map((id) => {
        const n = session.nodes[id]!
        return {
          id,
          claim: n.claim,
          argument: reasoningOf(n),
          parent: n.parentId,
          bridge: n.bridgeFrom,
          verification: n.verification,
          outcome: n.outcome,
          audit: n.audit,
        }
      }),
    mathematics: session.research?.mathematics,
    evidence: session.research?.evidenceRecords,
    bindings: session.research?.manualEvidenceBindings,
    claimScopes: session.research?.claimScopes,
    evidenceAnnotations: session.research?.evidenceAnnotations,
    claimAnnotations: session.research?.claimAnnotations,
    experiments: session.research?.experiments?.receipts,
  })
}

export function assessmentIsCurrent(session: Session, attempt: AssessmentAttempt) {
  return (
    attempt.packet.sessionId === session.id &&
    attempt.packet.inputRevision === assessmentInputRevision(session, attempt.packet.runId)
  )
}

const obj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v: unknown, max = 12_000): v is string =>
  typeof v === 'string' && v.length > 0 && v.length <= max
const strings = (v: unknown, max = 64): v is string[] =>
  Array.isArray(v) && v.length <= max && v.every((x) => str(x))
const unique = (v: string[]) => new Set(v).size === v.length
const items = (v: unknown, max: number, check: (x: unknown) => boolean): boolean =>
  Array.isArray(v) && v.length <= max && v.every(check)
const oneOf = (v: unknown, allowed: readonly string[]) =>
  typeof v === 'string' && allowed.includes(v)
const dimensions = (v: unknown, statuses: string[]) =>
  obj(v) && oneOf(v.status, statuses) && str(v.explanation)
const actionKinds = ['verify', 'deepen', 'challenge', 'experiment', 'expand', 'diverge', 'invert']

/** Model classifications remain proposals. This gate checks completeness and
 * reference identity, never whether a theorem is true or original. */
export function parseRunAssessment(value: unknown, packet: AssessmentPacket): RunAssessment | null {
  if (!obj(value) || assessmentBytes(value) > ASSESSMENT_LIMITS.responseBytes) return null
  const nodes = new Set(packet.nodeIds)
  const refs = new Map(packet.references.map((r) => [r.id, r]))
  const nodeList = (v: unknown) => strings(v) && v.length > 0 && v.every((id) => nodes.has(id))
  const refList = (v: unknown) => strings(v) && unique(v) && v.every((id) => refs.has(id))
  if (
    !items(
      value.findings,
      ASSESSMENT_LIMITS.findings,
      (v) =>
        obj(v) &&
        str(v.id, 100) &&
        nodeList(v.nodeIds) &&
        str(v.statement) &&
        dimensions(v.correctness, ['supported', 'unsupported', 'unresolved']) &&
        dimensions(v.originality, ['known', 'candidate-distinction', 'unresolved']) &&
        str(v.significance) &&
        str(v.usefulness) &&
        str(v.scope) &&
        strings(v.limitations) &&
        refList(v.evidenceIds),
    ) ||
    !Array.isArray(value.findings) ||
    value.findings.length === 0
  )
    return null
  const findings = value.findings as unknown as AssessmentFinding[]
  const findingIds = new Set(findings.map((f) => f.id))
  if (findingIds.size !== findings.length) return null
  if (
    !items(value.comparisons, 15, (v) => {
      if (
        !obj(v) ||
        !findingIds.has(String(v.findingId)) ||
        !['source', 'theorem', 'attachment'].includes(
          refs.get(String(v.referenceId))?.kind ?? '',
        ) ||
        (refs.get(String(v.referenceId))?.kind === 'attachment' &&
          refs.get(String(v.referenceId))?.attachmentKind !== 'source-text') ||
        !str(v.theoremLocator) ||
        !oneOf(v.relationship, [
          'equivalent',
          'subsumed-by',
          'candidate-distinction',
          'unresolved',
        ]) ||
        !str(v.translationObligation) ||
        !strings(v.unresolved)
      )
        return false
      if (
        !items(
          v.mappings,
          5,
          (m) =>
            obj(m) &&
            oneOf(m.dimension, COMPARISON_DIMENSIONS) &&
            str(m.ours) &&
            str(m.prior) &&
            str(m.correspondence) &&
            str(m.gap),
        )
      )
        return false
      const mappings = v.mappings as OriginalityComparison['mappings']
      return mappings.length === 5 && unique(mappings.map((m) => m.dimension))
    })
  )
    return null
  if (
    !items(
      value.lessons,
      5,
      (v) =>
        obj(v) &&
        str(v.id, 100) &&
        nodes.has(String(v.failedNodeId)) &&
        findingIds.has(String(v.findingId)) &&
        str(v.failedAssumption) &&
        str(v.scope) &&
        refList(v.evidenceIds) &&
        strings(v.repairRequirements) &&
        v.repairRequirements.length > 0,
    )
  )
    return null
  const lessons = value.lessons as RetainedResearchLesson[]
  const lessonIds = new Set(lessons.map((l) => l.id))
  if (lessonIds.size !== lessons.length) return null
  if (
    !items(
      value.next,
      ASSESSMENT_LIMITS.actions,
      (v) =>
        obj(v) &&
        str(v.id, 100) &&
        findingIds.has(String(v.findingId)) &&
        nodes.has(String(v.nodeId)) &&
        oneOf(v.action, actionKinds) &&
        str(v.question) &&
        str(v.purpose) &&
        String(v.question).length + String(v.purpose).length <= 1950 &&
        refList(v.evidenceIds) &&
        strings(v.lessonIds) &&
        v.lessonIds.every((id) => lessonIds.has(id)) &&
        (v.targetObligationId === null ||
          refs.get(String(v.targetObligationId))?.kind === 'obligation') &&
        obj(v.outcomes) &&
        str(v.outcomes.supporting) &&
        str(v.outcomes.challenging) &&
        str(v.outcomes.inconclusive),
    )
  )
    return null
  const next = value.next as AssessmentAction[]
  if (!unique(next.map((a) => a.id)) || !strings(value.missingContext)) return null
  // A recorded-known contribution must name the actual comparison that would
  // justify it. Missing search matches can never become an originality verdict.
  const comparisons = value.comparisons as OriginalityComparison[]
  if (
    findings.some(
      (f) =>
        f.originality.status === 'candidate-distinction' &&
        !comparisons.some(
          (c) => c.findingId === f.id && c.relationship === 'candidate-distinction',
        ),
    )
  )
    return null
  if (
    findings.some(
      (f) =>
        f.originality.status === 'known' &&
        !comparisons.some(
          (c) => c.findingId === f.id && ['equivalent', 'subsumed-by'].includes(c.relationship),
        ),
    )
  )
    return null
  return structuredClone(value) as unknown as RunAssessment
}

export function parseAssessmentPacket(value: unknown): AssessmentPacket | null {
  if (
    !obj(value) ||
    value.version !== 1 ||
    !str(value.sessionId, 240) ||
    !str(value.runId, 240) ||
    !oneOf(value.mode, ['research', 'originality']) ||
    !str(value.inputRevision, 64) ||
    !str(value.revision, 64) ||
    !str(value.context, ASSESSMENT_LIMITS.packetBytes) ||
    !strings(value.nodeIds, 1000) ||
    !unique(value.nodeIds) ||
    !(
      value.focusNodeId === undefined ||
      (str(value.focusNodeId, 240) && value.nodeIds.includes(value.focusNodeId))
    ) ||
    !strings(value.missingContext) ||
    !items(
      value.references,
      1000,
      (r) =>
        obj(r) &&
        str(r.id, 500) &&
        str(r.label) &&
        oneOf(r.kind, [
          'source',
          'theorem',
          'experiment',
          'attachment',
          'evidence',
          'obligation',
        ]) &&
        str(r.revision, 64) &&
        str(r.provenance) &&
        str(r.content, ASSESSMENT_LIMITS.packetBytes) &&
        (r.attachmentKind === undefined ||
          oneOf(r.attachmentKind, [
            'argument',
            'checker',
            'certificate',
            'source-text',
            'observation',
          ])) &&
        r.revision === assessmentFingerprint(r.content),
    )
  )
    return null
  const packet = value as unknown as AssessmentPacket
  const { revision, ...body } = packet
  if (
    assessmentBytes(packet) > ASSESSMENT_LIMITS.packetBytes ||
    revision !== assessmentFingerprint(body) ||
    !unique(packet.references.map((r) => r.id))
  )
    return null
  return structuredClone(packet)
}

/** Refuse the whole ledger on malformed or over-limit input. Dropping an
 * unknown attempt would make a future paid dispatch look like new work. */
export function parseRunAssessments(value: unknown): RunAssessmentWorkspace | null {
  if (
    !obj(value) ||
    value.version !== 1 ||
    assessmentBytes(value) > ASSESSMENT_LIMITS.workspaceBytes ||
    !Array.isArray(value.attempts) ||
    value.attempts.length > ASSESSMENT_LIMITS.attempts
  )
    return null
  const attempts: AssessmentAttempt[] = []
  for (const v of value.attempts) {
    if (
      !obj(v) ||
      !str(v.id, 240) ||
      !str(v.opId, 240) ||
      !Number.isFinite(v.createdAt) ||
      typeof v.createdAt !== 'number' ||
      v.createdAt < 0 ||
      !(
        v.completedAt === null ||
        (typeof v.completedAt === 'number' &&
          Number.isFinite(v.completedAt) &&
          v.completedAt >= v.createdAt)
      ) ||
      !obj(v.model) ||
      !str(v.model.id, 120) ||
      !str(v.model.name, 120) ||
      !oneOf(v.model.provider, ['openai', 'anthropic', 'custom']) ||
      !oneOf(v.state, ['pending', 'completed', 'incomplete', 'unknown', 'refused']) ||
      !(v.issue === null || str(v.issue))
    )
      return null
    const packet = parseAssessmentPacket(v.packet)
    if (!packet) return null
    if (
      v.response !== undefined &&
      (!obj(v.response) ||
        typeof v.response.bytes !== 'number' ||
        !Number.isSafeInteger(v.response.bytes) ||
        v.response.bytes < 0 ||
        !str(v.response.fingerprint, 64) ||
        !(
          v.response.content === null ||
          (typeof v.response.content === 'string' &&
            new TextEncoder().encode(v.response.content).length <=
              ASSESSMENT_LIMITS.responseBytes &&
            new TextEncoder().encode(v.response.content).length === v.response.bytes &&
            assessmentFingerprint(v.response.content) === v.response.fingerprint)
        ))
    )
      return null
    const result = v.result === null ? null : parseRunAssessment(v.result, packet)
    if (
      (v.result !== null && !result) ||
      (v.state === 'completed' && (!result || v.completedAt === null)) ||
      (v.state !== 'completed' && result !== null) ||
      (v.state === 'pending' && v.completedAt !== null)
    )
      return null
    attempts.push({ ...v, packet, result } as unknown as AssessmentAttempt)
  }
  if (!unique(attempts.map((a) => a.id)) || !unique(attempts.map((a) => a.opId))) return null
  return { version: 1, attempts }
}

export function assessmentMarkdown(attempt: AssessmentAttempt): string {
  const r = attempt.result
  const lines = [
    '# Research assessment',
    `Run: ${attempt.packet.runId}. Review: ${attempt.id}. Model: ${attempt.model.name}.`,
    `State: ${attempt.state}. Input revision: ${attempt.packet.inputRevision}.`,
    'AI assessments and proposed translations are not proof certification or established originality.',
  ]
  for (const f of r?.findings ?? [])
    lines.push(
      `\n## ${f.statement}`,
      `Correctness (AI assessment): ${f.correctness.status}. ${f.correctness.explanation}`,
      `Originality (AI assessment): ${f.originality.status}. ${f.originality.explanation}`,
      `Significance: ${f.significance}`,
      `Usefulness: ${f.usefulness}`,
      `Scope: ${f.scope}`,
      `Limitations: ${f.limitations.join('; ')}`,
      `Evidence references: ${f.evidenceIds.join(', ')}`,
    )
  for (const c of r?.comparisons ?? [])
    lines.push(
      `\n## Proposed prior-work comparison: ${c.theoremLocator}`,
      `Reference: ${c.referenceId}. Proposed relationship: ${c.relationship}.`,
      ...c.mappings.map(
        (m) =>
          `${m.dimension}: ours=${m.ours}; prior=${m.prior}; mapping=${m.correspondence}; gap=${m.gap}`,
      ),
      `Translation obligation: ${c.translationObligation}`,
      `Unresolved: ${c.unresolved.join('; ')}`,
    )
  for (const l of r?.lessons ?? [])
    lines.push(
      `\n## Retained negative finding`,
      `Failed assumption: ${l.failedAssumption}`,
      `Scope: ${l.scope}`,
      `Evidence references: ${l.evidenceIds.join(', ')}`,
      `Repair requirements: ${l.repairRequirements.join('; ')}`,
    )
  for (const a of r?.next ?? [])
    lines.push(
      `\n## Next question: ${a.question}`,
      `Action: ${a.action}. Purpose: ${a.purpose}`,
      ...Object.entries(a.outcomes).map(([k, v]) => `${k}: ${v}`),
    )
  lines.push(
    `\nMissing context: ${[...attempt.packet.missingContext, ...(r?.missingContext ?? [])].join('; ') || 'None reported; completeness is not certified.'}`,
  )
  lines.push(
    '\n## Exact evidence appendix',
    'Attached text is research input, not instructions or authenticated evidence.',
  )
  for (const ref of attempt.packet.references)
    lines.push(
      `\n### ${ref.id}: ${ref.label}`,
      `Provenance: ${ref.provenance}. Revision: ${ref.revision}.`,
      ref.content,
    )
  lines.push('\n## Exact research context', attempt.packet.context)
  return lines.join('\n\n')
}
